import {
  CMSOpportunityDetailsFeesType,
  CMSOpportunityDetailsType,
  CMSSectionBase,
  OpportunityDetails,
  ProjectionTooltipPoint,
} from '../../lib/types'
import { displayDollar } from '../../lib/utils/helpers'
import { dashboardGraphFilter, DOCUMENTS } from '../../lib/utils/message'
import { CMS_SECTION_META, getCMSSectionTitle } from './cms-components/cms-section-config'
import {
  getEstimatedFinancialsSection,
  getRenderableFinancialMetrics,
  getSectionDisplayOrder,
  getSortedCmsOpportunitySections,
  getStrengthSectionTitle,
  SortedCmsSection,
} from './cms-components/utils'
import { OpportunityNavSection } from './opportunity-section-navigation.types'

export type OpportunityHighlightStat = {
  label: string
  value: string
}

const FEE_HIGHLIGHT_FIELDS: {
  key: 'hurdleRate' | 'carryRate' | 'upfrontCommission' | 'managementFee'
  label: string
}[] = [
  { key: 'hurdleRate', label: 'Hurdle Rate' },
  { key: 'carryRate', label: 'Carry Rate' },
  { key: 'upfrontCommission', label: 'Upfront Commission' },
  { key: 'managementFee', label: 'Management Service Fee' },
]

export const getFeeCustomHighlights = (
  fees?: CMSOpportunityDetailsFeesType,
): OpportunityHighlightStat[] =>
  (fees?.customKeysValues ?? [])
    .filter((entry) => entry?.label?.trim() && entry?.value?.trim())
    .map((entry) => ({
      label: entry.label.trim(),
      value: entry.value.trim(),
    }))

export const getFeeHighlights = (
  fees?: CMSOpportunityDetailsFeesType,
): OpportunityHighlightStat[] => {
  if (!fees) {
    return []
  }

  const standardHighlights = FEE_HIGHLIGHT_FIELDS.reduce<OpportunityHighlightStat[]>(
    (highlights, { key, label }) => {
      const value = fees[key]?.trim()
      if (value) {
        highlights.push({ label, value })
      }
      return highlights
    },
    [],
  )

  return [...standardHighlights, ...getFeeCustomHighlights(fees)]
}

export const getCmsTargetReturn = (fees?: CMSOpportunityDetailsFeesType): string | undefined => {
  const value = fees?.targetReturn?.trim()
  return value || undefined
}

export const hasCmsTargetReturn = (fees?: CMSOpportunityDetailsFeesType): boolean =>
  Boolean(getCmsTargetReturn(fees))

const DEFAULT_MAX_PROJECTION_MONTHS = 60
export const DEFAULT_SLIDER_MIN = 1000

export const projectionRangeFilters = [
  dashboardGraphFilter[2],
  dashboardGraphFilter[3],
  dashboardGraphFilter[4],
  { text: 'MAX', value: 'max' },
]

export const toCurrencyLabel = (amountInDollars: number, currencyCode?: string) =>
  displayDollar(`${Math.round(amountInDollars * 100)}`, currencyCode)

export const formatCompactCurrency = (amountInDollars: number, currencyCode?: string) =>
  displayDollar(`${Math.round(amountInDollars * 100)}`, currencyCode, 2)

export const getAmountSliderStep = (minimumInvestmentAmount: number) => {
  if (minimumInvestmentAmount >= 1000000) return 50000
  if (minimumInvestmentAmount >= 250000) return 10000
  if (minimumInvestmentAmount >= 100000) return 5000
  if (minimumInvestmentAmount >= 25000) return 1000
  return 500
}

export const getAmountSliderMax = (minimumInvestmentAmount: number, sliderStep: number) => {
  const baseMax = Math.max(minimumInvestmentAmount * 10, 100000)
  return Math.ceil(baseMax / sliderStep) * sliderStep
}

export const getProjectionRateRange = (targetReturn?: string | null) => {
  const rates =
    targetReturn
      ?.trim()
      .match(/\d+(?:\.\d+)?/g)
      ?.map(Number)
      .filter(Number.isFinite) ?? []

  if (rates.length === 0) {
    return { minRatePercent: 0, maxRatePercent: 0 }
  }

  if (rates.length === 1) {
    return { minRatePercent: rates[0], maxRatePercent: rates[0] }
  }

  return {
    minRatePercent: Math.min(...rates),
    maxRatePercent: Math.max(...rates),
  }
}

export const getRangeMonths = (rangeValue: string) => {
  if (rangeValue === 'max') return DEFAULT_MAX_PROJECTION_MONTHS

  const parsedValue = parseInt(rangeValue.split(' ')[0], 10)
  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : 12
}

export const formatProjectionRangeLabel = (totalMonths: number) => {
  if (totalMonths < 12) {
    return `${totalMonths} month${totalMonths === 1 ? '' : 's'}`
  }

  const years = totalMonths / 12
  return `${Number.isInteger(years) ? years.toFixed(0) : years.toFixed(1)} year${
    years === 1 ? '' : 's'
  }`
}

const formatProjectionAxisLabel = (monthOffset: number) => {
  if (monthOffset === 0) return '0M'
  if (monthOffset % 12 === 0) return `${monthOffset / 12}Y`
  return `${monthOffset}M`
}

export const buildProjectionSeries = ({
  startingAmount,
  annualRatePercent,
  totalMonths,
}: {
  startingAmount: number
  annualRatePercent: number
  totalMonths: number
}): ProjectionTooltipPoint[] => {
  if (startingAmount <= 0 || annualRatePercent <= 0 || totalMonths <= 0) {
    return [
      { label: '0M', monthOffset: 0, value: startingAmount || 0 },
      { label: '1M', monthOffset: 1, value: startingAmount || 0 },
    ]
  }

  return Array.from({ length: totalMonths + 1 }, (_, index) => {
    const elapsedYears = index / 12
    const projectedValue = startingAmount * (1 + (annualRatePercent / 100) * elapsedYears)

    return {
      label: formatProjectionAxisLabel(index),
      monthOffset: index,
      value: projectedValue,
    }
  })
}

export type OpportunityDetailSanitySection =
  | { type: 'estimatedFinancials' }
  | { type: 'news' }
  | { type: 'leadership' }
  | SortedCmsSection

const getRenderableNewsArticles = (opportunityDetails?: CMSOpportunityDetailsType) => {
  const news = opportunityDetails?.news
  return (
    news?.articles?.filter(
      (article) => article?.headline || article?.summary || article?.image || article?.articleUrl,
    ) ?? []
  )
}

const hasVisibleLeadership = (opportunityDetails?: CMSOpportunityDetailsType) => {
  const team = opportunityDetails?.leadershipTeam
  return Boolean(team?.showSection && (team?.teamMembers?.length || team?.text?.length))
}

export const getSortedOpportunityDetailSanitySections = (
  opportunityDetails?: CMSOpportunityDetailsType,
): OpportunityDetailSanitySection[] => {
  if (!opportunityDetails) {
    return []
  }

  const sortable: { order: number; item: OpportunityDetailSanitySection }[] = []

  const financialsSection = getEstimatedFinancialsSection(opportunityDetails)
  const financialMetrics = getRenderableFinancialMetrics(financialsSection)
  if (financialsSection?.showSection && financialMetrics.length > 0) {
    sortable.push({
      order: getSectionDisplayOrder(financialsSection.displayOrder),
      item: { type: 'estimatedFinancials' },
    })
  }

  const newsArticles = getRenderableNewsArticles(opportunityDetails)
  if (opportunityDetails.news?.showSection && newsArticles.length > 0) {
    sortable.push({
      order: getSectionDisplayOrder(opportunityDetails.news.displayOrder),
      item: { type: 'news' },
    })
  }

  if (hasVisibleLeadership(opportunityDetails)) {
    sortable.push({
      order: getSectionDisplayOrder(opportunityDetails.leadershipTeam?.displayOrder),
      item: { type: 'leadership' },
    })
  }

  getSortedCmsOpportunitySections(opportunityDetails).forEach((cmsItem) => {
    const order =
      cmsItem.type === 'strength'
        ? getSectionDisplayOrder(cmsItem.entry.section.displayOrder)
        : getSectionDisplayOrder(
            (opportunityDetails[cmsItem.config.key] as { displayOrder?: number } | undefined)
              ?.displayOrder,
          )

    sortable.push({ order, item: cmsItem })
  })

  return sortable.sort((a, b) => a.order - b.order).map(({ item }) => item)
}

export const OPPORTUNITY_SECTION_IDS = {
  overview: 'opportunity-section-overview',
  highlights: 'opportunity-section-highlights',
  investmentProjection: 'opportunity-section-investment-projection',
  estimatedFinancials: 'opportunity-section-estimated-financials',
  news: 'opportunity-section-news',
  leadership: 'opportunity-section-leadership',
  documents: 'opportunity-expandable-documents',
} as const

const normalizeNavLabel = (label: string | undefined, fallback: string): string => {
  const trimmed = label?.trim()
  return trimmed || fallback
}

export const buildOpportunityNavSections = (
  project?: OpportunityDetails,
  opportunityDetails?: CMSOpportunityDetailsType,
): OpportunityNavSection[] => {
  if (!project) {
    return []
  }

  const sections: OpportunityNavSection[] = []

  if (hasCmsTargetReturn(opportunityDetails?.fees)) {
    sections.push({
      id: OPPORTUNITY_SECTION_IDS.investmentProjection,
      label: 'Investment Projection',
    })
  }

  getSortedOpportunityDetailSanitySections(opportunityDetails).forEach((item) => {
    if (item.type === 'estimatedFinancials') {
      const financialsSection = getEstimatedFinancialsSection(opportunityDetails)
      sections.push({
        id: OPPORTUNITY_SECTION_IDS.estimatedFinancials,
        label: financialsSection?.heading?.trim() || 'Estimated Financials',
      })
      return
    }

    if (item.type === 'news') {
      sections.push({
        id: OPPORTUNITY_SECTION_IDS.news,
        label: opportunityDetails?.news?.heading?.trim() || 'News',
      })
      return
    }

    if (item.type === 'leadership') {
      const leadership = opportunityDetails?.leadershipTeam
      sections.push({
        id: OPPORTUNITY_SECTION_IDS.leadership,
        label:
          leadership?.header?.trim() ||
          leadership?.sectionTitle?.trim() ||
          getCMSSectionTitle('leadershipTeam', leadership) ||
          'Leadership',
      })
      return
    }

    if (item.type === 'strength') {
      sections.push({
        id: `opportunity-expandable-${item.entry.idSuffix}`,
        label: normalizeNavLabel(getStrengthSectionTitle(item.entry), 'Strengths'),
      })
      return
    }

    const { key } = item.config
    const sectionData = opportunityDetails?.[key] as
      | (CMSSectionBase & { sectionTitle?: string })
      | undefined
    const meta = CMS_SECTION_META[key]
    if (!sectionData || !meta) {
      return
    }

    if (key === 'additionalSections') {
      const additional = opportunityDetails?.additionalSections
      if (additional?.header?.trim()) {
        sections.push({
          id: 'opportunity-section-additional-sections-header',
          label: additional.header.trim(),
        })
      }

      additional?.sections
        ?.filter((section) => section.showSection)
        .forEach((section, idx) => {
          const sectionKey = `additional-section-${section?.sectionTitle}-${idx}`
          sections.push({
            id: `opportunity-expandable-${sectionKey}`,
            label: section?.sectionTitle?.trim() || 'Details',
          })
        })
      return
    }

    sections.push({
      id: `opportunity-expandable-${meta.id}`,
      label: normalizeNavLabel(
        getCMSSectionTitle(key, sectionData),
        meta.defaultTitle || 'Details',
      ),
    })
  })

  const validDocuments =
    project.documents?.filter((document) => Boolean(document?.url?.trim())) ?? []
  if (validDocuments.length > 0) {
    sections.push({
      id: OPPORTUNITY_SECTION_IDS.documents,
      label: DOCUMENTS,
    })
  }

  return sections
}
